import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getProfileHeightString,
  getProfileWeightString,
  humanize,
  isUndefinedNullEmpty,
  SbCurrentUser,
} from '../submission_boards/component/SbModel';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LayoutGridIcon,
  Maximize2Icon,
  Minimize2Icon,
  PlayIcon,
  TextIcon,
  Play,
  WeightIcon,
  MoveVerticalIcon,
} from 'lucide-react';
import { useIsMobile } from '../submission_boards/component/useIsMobile';
import FilePreviewer from '../submission_kits/components/FilePreviewer';
import { KitDocumentModel } from '../submission_kits/components/KitModels';
import ViewMediaModel from '../submission_kits/components/ViewMediaModel';

interface WardrobeKitProps {
  currentUser: SbCurrentUser;
  user: any;
  showProfileHeader: (show: boolean) => void;
}
// component to show profile photo/age/weight/height
// used by SbDetails and SbDetailsRecipient
export const WardrobeKit: React.FC<WardrobeKitProps> = ({ currentUser, user, showProfileHeader }) => {
  const isMobile = useIsMobile();
  const [gridLayout, setgridLayout] = useState(true);
  const [wardrobeKitDocuments, setwardrobeKitDocuments] = useState(
    []
  );
  const [featureImage, setfeatureImage] = useState(null);
  const [showMediaBig, setshowMediaBig] = useState<KitDocumentModel | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(0);
  const [showTopProfileHeader, setShowTopProfileHeader] = useState(true)

  useEffect(() => {
    let kit_documents = user?.wardrobe_kit?.wardrobe_kit_documents;
    if (kit_documents != undefined && kit_documents.length > 0) {
      let sortedDocuments = [...kit_documents].sort((a, b) => {
        if (a.tag === null || b.tag === null) return 1;
        if (a.tag === "" && b.tag !== "") return 1;
        if (a.tag !== "" && b.tag === "") return -1;
        if (a.tag === "" || b.tag === "") return 1;
        return a.tag.localeCompare(b.tag, undefined, { numeric: true });
      });
      if (sortedDocuments[0].tag !== "" && !isMobile) {
        const [firstItem, ...remainingItems] = sortedDocuments;
        setfeatureImage(firstItem);
        setwardrobeKitDocuments(remainingItems);
      } else {
        setwardrobeKitDocuments(sortedDocuments);
      }
    }
  }, []);
  useEffect(() => {
    let kit_documents = user?.wardrobe_kit?.wardrobe_kit_documents;
    if (kit_documents != undefined && kit_documents.length > 0) {
      let sortedDocuments = [...kit_documents].sort((a, b) => {
        if (a.tag === null || b.tag === null) return 1;
        if (a.tag === "" && b.tag !== "") return 1;
        if (a.tag !== "" && b.tag === "") return -1;
        if (a.tag === "" || b.tag === "") return 1;
        return a.tag.localeCompare(b.tag, undefined, { numeric: true });
      });
      if (sortedDocuments[0].tag !== "" && !isMobile) {
        const [firstItem, ...remainingItems] = sortedDocuments;
        setfeatureImage(firstItem);
        setwardrobeKitDocuments(remainingItems);
      } else {
        setwardrobeKitDocuments(sortedDocuments);
      }
    }
  }, [isMobile]);

  // Helper function to format/round numeric values
  const formatValue = (value: any) => {
    if (value === null || value === undefined || value === '') return value;
    const strVal = String(value);
    // Strict numeric check: only digits, optional decimal point.
    if (/^\s*\d+(\.\d+)?\s*$/.test(strVal)) {
      return Number(strVal);
    }
    return value;
  };
  useEffect(() => {
    showProfileHeader(showTopProfileHeader);
  }, [showTopProfileHeader]);

  const renderImage = (index: number, no_of_items: number, feature: boolean) => {
    let mainKitDoc = undefined;
    let isEmpty = true;
    if (feature) {
      mainKitDoc = featureImage;
      isEmpty = !mainKitDoc;
    } else {
      mainKitDoc = wardrobeKitDocuments[index];
      isEmpty = !mainKitDoc;

    }

    return (
      <div className="flex flex-col gap-5 rounded-lg">
        {isEmpty ? (
          <></>
        ) : (
          <div
            className={
              'relative flex rounded-xl overflow-hidden' + (no_of_items == 1 ? ' flex-1 ' : ' ')
            }
            style={{
              width: no_of_items == 2 ? '9.8125rem' : '20rem',
              // height: no_of_items == 2 ? '9.8125rem' : '20rem',
              aspectRatio: 4 / 5
            }}
            onClick={(e) => {
              e.preventDefault();
              setshowMediaBig(mainKitDoc.kit_document);
            }}
          >
            <FilePreviewer
              dynamicHeight={false}
              object_fit={'object-contain object-top'}
              file_url={
                mainKitDoc.kit_document.cropped_file_url || mainKitDoc.kit_document.file_url
              }
              file_name={mainKitDoc.kit_document.file_name}
              file_content_type={mainKitDoc.kit_document.file_content_type}
            />
          </div>
        )}
      </div>
    );
  };

  const gridBoxItem = (title, value, icon: React.ReactNode) => {
    return (
      <div
        className={"flex flex-col  items-center gap-2-1 border border-blue-502 " +
          (title != null ? ' justify-start ' : ' justify-center ')
        }
        style={{
          padding: '0.375rem',
          borderRadius: '0.625rem',
          color: '#8CA2C280',
          backgroundColor: '#E9EEF6D9',
        }}
      >
        {title != null && (
          <div
            className="flex flex-col justify-end items-center gap-2-1 bg-white w-full"
            style={{
              padding: '0.75rem 0.5rem 0.375rem',
              borderRadius: '0.375rem 0.375rem 0.125rem 0.125rem',
            }}
          >
            <div className="font-normal font-semibold text-13 text-center text-blue-403">
              {title}
            </div>
          </div>
        )}
        <div className="flex flex-col justify--center items-start gap-2-1">
          <div
            className=" flex flex-row justify--center items-center gap-2-1 px-5 py-2-1 sm:py-6 text-lg primary-color"
            style={{
              minHeight: '1.875rem',
              // minWidth: '8.75rem',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {icon != null && <div id="icon">{icon}</div>}
            <div style={{
              wordWrap: 'break-word',
              whiteSpace: 'pre-line'
            }}>{isUndefinedNullEmpty(value) ? '-' : value}</div>
          </div>
        </div>
      </div>
    );
  };

  const listBoxItem = (title, value) => {
    return (
      <div className="flex flex-col items-start p-0 w-full">
        {title != null && (
          <div
            className="flex flex-row  gap-2-1 w-full"
            style={{
              paddingTop: '0.1875rem',
            }}
          >
            <div className="text-13 font-medium text-left text-blue-403 leading-5"
              style={{ lineHeight: '20px' }}>
              {title}</div>
          </div>
        )}
        <div className="flex flex-col primary-color font-regular text-lg" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
          {value || '-'}
        </div>
      </div>
    );
  };

  const toggleRender = () => {
    return (
      <div
        className="flex flex-row cursor-pointer items-center gap-1-1 rounded-full"
        style={{
          padding: '1rem 0.5rem 1rem 0.5rem',
          backgroundColor: '#A5B7D380',
          maxHeight: '1.625rem',
        }}
        onClick={() => {
          setgridLayout(!gridLayout);
        }}
      >
        <div
          className={
            'flex flex-col items-center gap-2 rounded-full ' + (gridLayout ? 'bg-white' : '')
          }
          style={{
            padding: '0.3125rem 1.125rem 0.3125rem 0.625rem',
            // margin: '0rem -0.625rem',
            zIndex: 1,
          }}
        >
          <LayoutGridIcon className="w-4 h-4" />
        </div>

        <div
          className={
            'flex flex-col items-center gap-2 rounded-full ' + (!gridLayout ? 'bg-white' : '')
          }
          style={{
            padding: '0.3125rem 0.875rem 0.3125rem 0.875rem',
            zIndex: 1,
          }}
        >
          <TextIcon className="w-4 h-4" />
        </div>
      </div>
    );
  };
  const handlePreviousMedia = () => {
    setCurrentMediaIndex((prev) => (prev > 0 ? prev - 1 : wardrobeKitDocuments.length - 1));
  };

  const handleNextMedia = () => {
    setCurrentMediaIndex((prev) => (prev < wardrobeKitDocuments.length - 1 ? prev + 1 : 0));
  };

  const renderMediaSection = () => {
    return <> {isMobile ? <>
      {wardrobeKitDocuments.length > 0 && (
        <div className="flex flex-col items-center p-0 gap-3 w-full border-b border-gray-300 pb-5">
          <div className="w-full rounded-xl relative overflow-hidden"
            style={{
              aspectRatio: wardrobeKitDocuments[currentMediaIndex].kit_document.file_content_type?.startsWith('video') ? 16 / 9 : 4 / 5,
              // width: '19rem',
              // height: '19rem'
            }}
            ref={panelRef}
            onTouchStart={handleTouchStartMedia}
            onTouchMove={handleTouchMoveMedia}
            onTouchEnd={handleTouchEndMedia} >
            <div className="w-full h-full relative"
              onClick={(e) => {
                e.preventDefault();
                setshowMediaBig(wardrobeKitDocuments[currentMediaIndex].kit_document);
              }} >
              <FilePreviewer
                key={`media-${currentMediaIndex}-${wardrobeKitDocuments[currentMediaIndex]?.kit_document.id
                  || wardrobeKitDocuments[currentMediaIndex]?.kit_document.file_url || currentMediaIndex}`}
                dynamicHeight={false}
                object_fit={'object-contain object-top'}
                file_url={wardrobeKitDocuments[currentMediaIndex].kit_document.cropped_file_url || wardrobeKitDocuments[currentMediaIndex].kit_document.file_url}
                file_name={wardrobeKitDocuments[currentMediaIndex].kit_document.file_name}
                file_content_type={wardrobeKitDocuments[currentMediaIndex].kit_document.file_content_type}
              />
              {wardrobeKitDocuments[currentMediaIndex].kit_document.file_content_type?.startsWith('video') && (
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 box-border flex flex-col justify-center items-center p-3 gap-2.5 w-12 h-12 rounded-full cursor-pointer" style={{ background: 'rgba(0, 0, 0, 0.2)', border: '1px solid #FFFFFF' }}>
                  <PlayIcon size={24} color="#FFFFFF" fill="#FFFFFF" />
                </div>
              )}
            </div>
          </div>

          {/* Media count label - Centered */}
          <div className="flex-none order-0 self-stretch flex-grow-0 text-center " style={{ color: '#0D1621', lineHeight: '1.875rem' }}>
            Media ({wardrobeKitDocuments.length})
          </div>

          {/* Pagination with arrows and dots */}
          {wardrobeKitDocuments.length > 1 && (
            <div className="flex flex-row items-center justify-center gap-2 w-full py-2">
              <button
                onClick={handlePreviousMedia}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                aria-label="Previous media"
              >
                <ChevronLeftIcon size={20} color="#4B5563" />
              </button>
              <div className="flex flex-row items-center gap-2">
                {wardrobeKitDocuments.map((_, index) => (
                  <div
                    key={index}
                    className={`rounded-full transition-all ${index === currentMediaIndex ? 'w-2 h-2 bg-blue-500' : 'w-1.5 h-1.5 bg-gray-300'}`}
                  />
                ))}
              </div>
              <button
                onClick={handleNextMedia}
                className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                aria-label="Next media"
              >
                <ChevronRightIcon size={20} color="#4B5563" />
              </button>
            </div>
          )}
        </div>
      )}
    </> : (<><div className="grid grid-cols-1 gap-4">
      {featureImage != null && renderImage(0, 1, true)}
    </div>
      <div className="grid grid-cols-2 gap-4">
        {wardrobeKitDocuments.map((itemkit, indexKit) => {
          return <>{renderImage(indexKit, 2, false)}</>;
        })}
      </div></>)}
    </>;
  }
  // media slider
  const panelRef = useRef(null);
  const MIN_SWIPE_DISTANCE = 50; // Distance in pixels to confirm a swipe-down close
  const [dragYMedia, setDragYMedia] = useState(0); // Tracks vertical drag distance (for visual feedback)
  // const touchStartY = useRef(0); // Stores the Y position when the touch starts
  const touchStartMedia = useRef({ x: 0, y: 0 });
  const handleTouchStartMedia = (e) => {
    e.stopPropagation();
    // Only track single touch starts on the dialog panel itself
    if (e.touches.length === 1) {
      touchStartMedia.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      setDragYMedia(0); // Reset drag distance
      panelRef.current.style.transition = 'none'; // Disable transition during drag
    }
  };

  const handleTouchMoveMedia = useCallback((e) => {
    e.stopPropagation();
    if (e.touches.length !== 1) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartMedia.current.y;

    // Only allow downward swiping (deltaY > 0)
    if (deltaY > 0) {
      //e.preventDefault(); // Prevent scrolling the main page while dragging the dialog
      setDragYMedia(deltaY);

      // Apply immediate visual translation via inline style
      // if (panelRef.current) {
      //   panelRef.current.style.transform = `translateY(${deltaY}px)`;
      // }
    }
  }, []);

  const handleTouchEndMedia = useCallback(
    (e) => {
      e.stopPropagation();
      if (e.changedTouches.length === 0) return;

      // Get the final touch position
      const touchEnd = {
        x: e.changedTouches[0].clientX,
        y: e.changedTouches[0].clientY,
      };

      // Calculate the total change in coordinates
      const deltaX = touchEnd.x - touchStartMedia.current.x;
      const deltaY = touchEnd.y - touchStartMedia.current.y;

      // Calculate the absolute distance moved in each axis
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      // --- Check for a deliberate swipe (must move more than the minimum distance) ---
      if (absDeltaX < MIN_SWIPE_DISTANCE && absDeltaY < MIN_SWIPE_DISTANCE) {
        // console.log('Only a small movement (tap or minor drag).');
        return;
      }

      // --- 1. Determine DOMINANT axis ---
      if (absDeltaX > absDeltaY) {
        // Horizontal swipe is dominant
        if (deltaX > 0) {
          handlePreviousMedia();
        } else {
          handleNextMedia();
        }
      } else {
      }
      setDragYMedia(0);
    },
    [dragYMedia]
  );

  // media slider end   
  return (
    <>
      <div
        className="flex flex-col bg-white rounded-2xl border border-blue-502 "
        style={{
          paddingTop: isMobile ? '2.5rem' : '2.5rem', // 40px 60px
          paddingBottom: isMobile ? '2.5rem' : '2.5rem', // 60px
          paddingLeft: isMobile ? '1.25rem' : '3.75rem', // 20px 60px
          paddingRight: isMobile ? '1.25rem' : '3.75rem', // 60px
          gap: isMobile ? '1rem' : '2.5rem', // 60px
        }}
      >
        <div className="flex flex-col sm:flex-row flex-1 justify-between items-center gap-4 border-b border-blue-502">
          <div className="flex flex-row flex-1 items-center">
            <div className={`w-full flex-1 m-0 ${isMobile ? 'h-auto text-3xl leading-9' : 'h-12 text-3xl leading-12'} font-medium tracking-tight`} style={{ color: '#576C89' }}>
              Wardrobe Kit</div>
          </div>
          {/* icons */}
          <div className="flex flex-row gap-1-1 pb-4">{toggleRender()}</div>
          {showTopProfileHeader ? <div
            className="w-7 h-7 cursor-pointer hidden sm:flex items-center justify-center  pb-4 pt-2"
            style={{
              color: '#576C89',
            }}
            onClick={() => {
              setShowTopProfileHeader(false);
            }}
          >
            <Maximize2Icon className="w-7 h-7" />
          </div> : <div
            className="w-7 h-7 cursor-pointer  hidden sm:flex items-center justify-center overflow-hidden"
            style={{
              color: '#576C89',
            }}
            onClick={() => {
              setShowTopProfileHeader(true);
            }}
          >
            <Minimize2Icon className="w-7 h-7" />
          </div>}
        </div>
        <div className="flex sm:hidden flex-row gap-10">
          {renderMediaSection()}
        </div>
        <div className="flex flex-col sm:flex-row gap-10">
          {/* Left Section */}
          <div className="flex flex-col gap-5 flex-1">
            <div className="flex flex-col sm:flex-row items-start gap-5 pb-5 border-b border-blue-502" >
              <div className="flex flex-col gap-1.5" style={{ width: isMobile ? '100%' : '170px' }}>
                <h3 className={`text-xl font-semibold ${isMobile ? 'whitespace-nowrap' : ''}`}
                  style={{
                    color: '#0D1621',
                    lineHeight: '150%',
                    width: isMobile ? '100%' : '170px',
                    height: isMobile ? 'auto' : '60px'
                  }}>
                  <div className="flex sm:block">
                    <span className="mr-1 sm:block sm:mr-0">Body</span>
                    <span className="sm:block">Measurements</span>
                  </div>
                </h3>
                <div className="text-sm font-normal" style={{ color: '#0D1621', lineHeight: '21px', width: isMobile ? '100%' : '170px', height: isMobile ? 'auto' : '21px' }}>
                  Physique
                </div>
              </div>
              {!gridLayout && (
                <div className={`flex w-full sm:w-auto ${isMobile ? 'flex-row' : 'flex-row'} flex-1 ${isMobile ? '' : 'pr-5'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                  {listBoxItem('Height', getProfileHeightString(user, currentUser))}
                  {listBoxItem('Weight', getProfileWeightString(user, currentUser))}
                </div>
              )}
              {gridLayout && (
                <div className="flex flex-col gap-1 flex-1 w-full">
                  <div className="grid grid-cols-2 gap-1 flex-1 w-full">

                    {gridBoxItem(
                      'Height',
                      getProfileHeightString(user, currentUser),
                      <MoveVerticalIcon className="h-5 w-5 text-gray-500" />
                    )}

                    {gridBoxItem(
                      'Weight',
                      getProfileWeightString(user, currentUser),
                      <WeightIcon className="h-5 w-5 text-gray-500" />
                    )}

                  </div>
                </div>
              )}

            </div>
            <div className="flex flex-col sm:flex-row items-start gap-5 pb-5 border-b border-blue-502" >
              <div className="flex flex-col gap-1.5" style={{ width: isMobile ? '100%' : '170px' }}>
                <h3 className="text-xl font-semibold" style={{ color: '#0D1621', lineHeight: '150%', width: isMobile ? '100%' : '170px', height: isMobile ? 'auto' : '60px' }}>
                  <div className="flex sm:block">
                    <span className="mr-1 sm:block sm:mr-0">Fitting</span>
                    <span className="sm:block">Measurements</span>
                  </div>
                </h3>
                <div className="text-sm font-normal" style={{ color: '#0D1621', lineHeight: '21px', width: isMobile ? '100%' : '170px', height: isMobile ? 'auto' : '21px' }}>
                  Clothing
                </div>
              </div>
              {!gridLayout && (
                <div className="flex flex-col flex-1 w-full sm:w-auto" style={{ gap: isMobile ? '20px' : '30px', paddingRight: isMobile ? '0' : '20px' }}>
                  {user.gender == 'male' && (
                    <>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Coat', formatValue(user.wardrobe_kit && user.wardrobe_kit.jacket))}
                        {listBoxItem('Neck', formatValue(user.wardrobe_kit && user.wardrobe_kit.neck))}
                        {listBoxItem('Sleeve', formatValue(user.wardrobe_kit && user.wardrobe_kit.sleeve))}
                      </div>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Shirt', formatValue(user.wardrobe_kit && user.wardrobe_kit.shirt))}
                        {listBoxItem('Waist', formatValue(user.wardrobe_kit && user.wardrobe_kit.waist_measurement))}
                        {listBoxItem('Inseam', formatValue(user.wardrobe_kit && user.wardrobe_kit.inseam))}
                      </div>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Shoes', formatValue(user.wardrobe_kit && user.wardrobe_kit.shoes))}
                        {listBoxItem('Hat', formatValue(user.wardrobe_kit && user.wardrobe_kit.hat))}
                        {listBoxItem('Gloves', formatValue(user.wardrobe_kit && user.wardrobe_kit.gloves))}
                      </div>
                    </>
                  )}
                  {user.gender == 'female' && (
                    <>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Dress', formatValue(user.wardrobe_kit && user.wardrobe_kit.dress))}
                        {listBoxItem('Coat', formatValue(user.wardrobe_kit && user.wardrobe_kit.jacket))}
                        {listBoxItem('Shirt', formatValue(user.wardrobe_kit && user.wardrobe_kit.shirt))}
                      </div>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Chest', formatValue(user.wardrobe_kit && user.wardrobe_kit.chest_measurement))}
                        {listBoxItem('Waist', formatValue(user.wardrobe_kit && user.wardrobe_kit.waist_measurement))}
                        {listBoxItem('Hip', formatValue(user.wardrobe_kit && user.wardrobe_kit.hip_measurement))}
                      </div>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Bra', formatValue(user.wardrobe_kit && user.wardrobe_kit.bra_size))}
                        {listBoxItem('Inseam', formatValue(user.wardrobe_kit && user.wardrobe_kit.inseam))}
                        <div className="flex flex-col items-start p-0 w-full"></div>
                      </div>
                      <div className={`flex w-full ${isMobile ? 'flex-row' : 'flex-row'}`} style={{ gap: isMobile ? '20px' : '30px' }}>
                        {listBoxItem('Shoes', formatValue(user.wardrobe_kit && user.wardrobe_kit.shoes))}
                        {listBoxItem('Hat', formatValue(user.wardrobe_kit && user.wardrobe_kit.hat))}
                        {listBoxItem('Gloves', formatValue(user.wardrobe_kit && user.wardrobe_kit.gloves))}
                      </div>
                    </>
                  )}
                </div>
              )}
              {gridLayout && (
                <>
                  {user.gender == 'male' && <div className="gap-1 flex flex-col flex-1 w-full">
                    <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                      {gridBoxItem('Coat', formatValue(user.wardrobe_kit && user.wardrobe_kit.jacket), null)}
                      {gridBoxItem('Neck', formatValue(user.wardrobe_kit && user.wardrobe_kit.neck), null)}
                      {gridBoxItem('Sleeve', formatValue(user.wardrobe_kit && user.wardrobe_kit.sleeve), null)}
                    </div>
                    <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                      {gridBoxItem('Shirt', formatValue(user.wardrobe_kit && user.wardrobe_kit.shirt), null)}
                      {gridBoxItem('Waist', formatValue(user.wardrobe_kit && user.wardrobe_kit.waist_measurement), null)}
                      {gridBoxItem('Inseam', formatValue(user.wardrobe_kit && user.wardrobe_kit.inseam), null)}
                    </div>
                    <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                      {gridBoxItem('Shoes', formatValue(user.wardrobe_kit && user.wardrobe_kit.shoes), null)}
                      {gridBoxItem('Hat', formatValue(user.wardrobe_kit && user.wardrobe_kit.hat), null)}
                      {gridBoxItem('Gloves', formatValue(user.wardrobe_kit && user.wardrobe_kit.gloves), null)}
                    </div>
                  </div>}
                  {user.gender == 'female' && <div className="gap-1 flex flex-col flex-1 w-full">
                    {isMobile && <>
                      <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                        {gridBoxItem('Dress', formatValue(user.wardrobe_kit && user.wardrobe_kit.dress), null)}
                        {gridBoxItem('Coat', formatValue(user.wardrobe_kit && user.wardrobe_kit.jacket), null)}
                        {gridBoxItem('Shirt', formatValue(user.wardrobe_kit && user.wardrobe_kit.shirt), null)}
                      </div>
                      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-2'} gap-1 flex-1 w-full`}>
                        {gridBoxItem(
                          'Chest',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.chest_measurement),
                          null
                        )}
                        {gridBoxItem(
                          'Waist',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.waist_measurement),
                          null
                        )}
                      </div>
                      <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                        {gridBoxItem(
                          'Hip',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.hip_measurement),
                          null
                        )}
                        {gridBoxItem('Inseam', formatValue(user.wardrobe_kit && user.wardrobe_kit.inseam), null)}
                        {gridBoxItem('Bra', formatValue(user.wardrobe_kit && user.wardrobe_kit.bra_size), null)}
                      </div>
                      <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                        {gridBoxItem('Shoes', formatValue(user.wardrobe_kit && user.wardrobe_kit.shoes), null)}
                        {gridBoxItem('Hat', formatValue(user.wardrobe_kit && user.wardrobe_kit.hat), null)}
                        {gridBoxItem('Gloves', formatValue(user.wardrobe_kit && user.wardrobe_kit.gloves), null)}
                      </div>

                    </>}
                    {!isMobile && <>
                      <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-3'} gap-1 flex-1 w-full`}>
                        {gridBoxItem('Dress', formatValue(user.wardrobe_kit && user.wardrobe_kit.dress), null)}
                        {gridBoxItem('Coat', formatValue(user.wardrobe_kit && user.wardrobe_kit.jacket), null)}
                        {gridBoxItem('Shirt', formatValue(user.wardrobe_kit && user.wardrobe_kit.shirt), null)}
                      </div>
                      <div className={`grid ${isMobile ? 'grid-cols-4' : 'grid-cols-4'} gap-1 flex-1 w-full`}>
                        {gridBoxItem(
                          'Chest',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.chest_measurement),
                          null
                        )}
                        {gridBoxItem(
                          'Waist',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.waist_measurement),
                          null
                        )}
                        {gridBoxItem(
                          'Hip',
                          formatValue(user.wardrobe_kit && user.wardrobe_kit.hip_measurement),
                          null
                        )}
                        {gridBoxItem('Bra', formatValue(user.wardrobe_kit && user.wardrobe_kit.bra_size), null)}
                      </div>
                      <div className={`grid ${isMobile ? 'grid-cols-4' : 'grid-cols-4'} gap-1 flex-1 w-full`}>
                        {gridBoxItem('Inseam', formatValue(user.wardrobe_kit && user.wardrobe_kit.inseam), null)}
                        {gridBoxItem('Shoes', formatValue(user.wardrobe_kit && user.wardrobe_kit.shoes), null)}
                        {gridBoxItem('Hat', formatValue(user.wardrobe_kit && user.wardrobe_kit.hat), null)}
                        {gridBoxItem('Gloves', formatValue(user.wardrobe_kit && user.wardrobe_kit.gloves), null)}
                      </div>

                    </>}
                  </div>}
                </>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-start gap-5 border-blue-502" >
              <div className="flex flex-col gap-1.5" style={{ width: isMobile ? '100%' : '170px', height: isMobile ? 'auto' : '52px' }}>
                <h3 className="text-xl font-semibold" style={{ color: '#0D1621', lineHeight: '130%', width: isMobile ? '100%' : '170px', height: isMobile ? 'auto' : '52px' }}>
                  <div className="flex sm:block">
                    <span className="mr-1 sm:block sm:mr-0">Other</span>
                    <span className="sm:block">Information</span>
                  </div>
                </h3>
              </div>
              {!gridLayout && (
                <div className="flex flex-col flex-1" style={{ gap: isMobile ? '20px' : '30px', paddingRight: isMobile ? '0' : '20px', width: isMobile ? '100%' : '430px' }}>
                  {listBoxItem('Piercings', user.wardrobe_kit && user.wardrobe_kit.piercings)}
                  {listBoxItem('Textile Allergies', user.wardrobe_kit && user.wardrobe_kit.textile_allergies)}
                  {listBoxItem('Tattoos', user.wardrobe_kit && user.wardrobe_kit.tattoos)}
                  {listBoxItem('Notes', user.wardrobe_kit && user.wardrobe_kit.notes)}
                </div>
              )}
              {gridLayout && (<>
                <div className="grid grid-cols-1 gap-1 flex-1 w-full ">
                  <div className="grid grid-cols-1 gap-1 flex-1 w-full ">
                    {gridBoxItem('Piercings', user.wardrobe_kit && user.wardrobe_kit.piercings, null)}
                    {gridBoxItem('Textile Allergies', user.wardrobe_kit && user.wardrobe_kit.textile_allergies, null)}
                  </div>
                  <div className="grid grid-cols-1 gap-1 flex-1 w-full ">
                    {gridBoxItem('Tattoos', user.wardrobe_kit && user.wardrobe_kit.tattoos, null)}
                    {gridBoxItem('Notes', user.wardrobe_kit && user.wardrobe_kit.notes, null)}
                  </div>
                </div>
              </>
              )}
            </div>
          </div>

          {/* Right Section (Images) */}
          <div
            className="hidden sm:flex flex-col gap-1-1"
            style={{
              width: '20rem',
            }}
          >
            {renderMediaSection()}
          </div>
        </div>
        {showMediaBig != null && (
          <>
            <ViewMediaModel
              kit_document={showMediaBig}
              dialogCloseHandler={function (): void {
                setshowMediaBig(null);
              }}
            />
          </>
        )}
      </div>{' '}
    </>
  );
};
